import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'antd'
import type { CreateOrgPayload } from '../../types'
import { normalizeOrganizationPlan } from '../../utils'
import NoticeBanner from '../shared/NoticeBanner'
import OrganizationNameField from './OrganizationNameField'
import ModalOfficeAddress from './ModalOfficeAddress'
import './NewOrganization.css'

interface CreateOrgFormValues {
  companyName: string
  street: string
  city: string
  state: string
  county: string
  zip: string
}

interface CreateOrgModalProps {
  open: boolean
  submitting?: boolean
  sourcePlan?: string
  onCancel: () => void
  onSubmit: (payload: CreateOrgPayload) => void | Promise<void>
}

const CreateOrgModal: React.FC<CreateOrgModalProps> = ({
  open,
  submitting = false,
  sourcePlan,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<CreateOrgFormValues>()
  const [duplicateName, setDuplicateName] = useState(false)
  const [checkingName, setCheckingName] = useState(false)
  const [submitPending, setSubmitPending] = useState(false)

  useEffect(() => {
    if (!open) return
    form.resetFields()
    setDuplicateName(false)
    setCheckingName(false)
    setSubmitPending(false)
  }, [open, form])

  const handleSubmit = async () => {
    if (submitting || submitPending || duplicateName || checkingName) return
    setSubmitPending(true)
    try {
      const values = await form.validateFields()
      await onSubmit({
        companyName: values.companyName.trim(),
        street: values.street?.trim() || '',
        city: values.city?.trim() || '',
        state: values.state?.trim() || '',
        county: values.county?.trim() || '',
        zip: values.zip?.trim() || '',
      })
    } catch {
      // validation errors shown by form
    } finally {
      if (!submitting) {
        setSubmitPending(false)
      }
    }
  }

  return (
    <Modal
      open={open}
      title="Create a new organization"
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitting || submitPending}
          disabled={duplicateName || checkingName || submitting || submitPending}
        >
          Create organization
        </Button>,
      ]}
      width={600}
      className="um-refactor__modal um-new-org__modal"
      destroyOnClose
    >
      <p className="um-refactor__modal-desc">
        Open a new office as its own organization. You&apos;ll automatically be its admin.
      </p>

      <Form form={form} layout="vertical" requiredMark={false} className="um-new-org__form">
        <OrganizationNameField
          onDuplicateChange={setDuplicateName}
          onCheckingChange={setCheckingName}
        />
        <ModalOfficeAddress />
      </Form>

      <NoticeBanner variant="info">
        · Its data is fully isolated from your other organizations
        <br />
        · It is linked to them only through you as the common owner
      </NoticeBanner>
      {normalizeOrganizationPlan(sourcePlan) !== 'enterprise' && (
        <NoticeBanner variant="warn">
          Billing: a new organization may require its own subscription.
        </NoticeBanner>
      )}
    </Modal>
  )
}

export default CreateOrgModal
